import React, { useEffect, useState, useCallback } from "react";
import { PlayCircle } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import { supabase } from "../../Services/supabaseClient";
import { getScenarios, runScenario } from "../payrollService";

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const initialForm = {
  scenario_name: "",
  salary_increase_pct: "",
  headcount_increase: "",
};

export default function PayrollScenarios() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(initialForm);

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      const data = await getScenarios(companyId);
      setRows(data || []);
    } catch (err) {
      setMsg(err.message || "Failed to load payroll scenarios");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRun(e) {
    e.preventDefault();
    try {
      setRunning(true);
      setMsg("");

      const {
        data: { user },
      } = await supabase.auth.getUser();

      await runScenario({
        company_id: companyId,
        scenario_name: form.scenario_name,
        assumptions: {
          salary_increase_pct: Number(form.salary_increase_pct || 0),
          headcount_increase: Number(form.headcount_increase || 0),
        },
        created_by: user?.id || null,
      });

      setForm(initialForm);
      await load();
      setMsg("Payroll scenario run successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to run scenario");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Scenarios</p>
          <h3>{rows.length}</h3>
          <span>Total saved simulations</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Latest Gross</p>
          <h3>
            {rows.length
              ? formatMoney(rows[0]?.result_summary?.total_gross || 0)
              : "0.00"}
          </h3>
          <span>Most recent scenario gross pay</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Latest Net</p>
          <h3>
            {rows.length
              ? formatMoney(rows[0]?.result_summary?.total_net || 0)
              : "0.00"}
          </h3>
          <span>Most recent scenario net pay</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Latest Headcount</p>
          <h3>
            {rows.length ? rows[0]?.result_summary?.employee_count || 0 : 0}
          </h3>
          <span>Most recent simulated headcount</span>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Run Payroll Scenario</h3>
            <p>Simulate the financial effect of salary or headcount changes.</p>
          </div>
        </div>

        <form className="payroll-form" onSubmit={handleRun}>
          <div className="payroll-grid payroll-grid-3">
            <label>
              Scenario Name
              <input
                value={form.scenario_name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, scenario_name: e.target.value }))
                }
                placeholder="e.g. 10% Salary Increase"
                required
              />
            </label>

            <label>
              Salary Increase (%)
              <input
                type="number"
                step="0.01"
                value={form.salary_increase_pct}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    salary_increase_pct: e.target.value,
                  }))
                }
                placeholder="e.g. 10"
              />
            </label>

            <label>
              Headcount Increase
              <input
                type="number"
                value={form.headcount_increase}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    headcount_increase: e.target.value,
                  }))
                }
                placeholder="e.g. 2"
              />
            </label>
          </div>

          <div className="payroll-form-actions">
            <button type="submit" className="primary-btn" disabled={running}>
              <PlayCircle size={16} />
              {running ? "Running..." : "Run Scenario"}
            </button>
          </div>
        </form>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Scenario Results</h3>
            <p>Saved scenario outcomes for comparison and planning.</p>
          </div>
        </div>

        <div className="payroll-table-wrap">
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Headcount</th>
                <th>Total Gross</th>
                <th>Total Deductions</th>
                <th>Total Net</th>
                <th>Employer Cost</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const result = row.result_summary || {};
                return (
                  <tr key={row.id}>
                    <td>{row.scenario_name}</td>
                    <td>
                      {Number(result.employee_count || 0).toLocaleString()}
                    </td>
                    <td>{formatMoney(result.total_gross || 0)}</td>
                    <td>{formatMoney(result.total_deductions || 0)}</td>
                    <td>{formatMoney(result.total_net || 0)}</td>
                    <td>{formatMoney(result.total_employer_cost || 0)}</td>
                    <td>{new Date(row.created_at).toLocaleString()}</td>
                  </tr>
                );
              })}

              {!rows.length && !busy && (
                <tr>
                  <td colSpan="7">No payroll scenarios found.</td>
                </tr>
              )}

              {busy && (
                <tr>
                  <td colSpan="7">Loading...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
